import { applicationsClient } from '@terra/shared-rest-clients-applications';
import type { ApplicationCredential } from '../hooks/useApplicationCredentials';
import { getCurrentGatewayUrl } from './environmentDetection';
import { getAuthHeaders } from './authHeaders';

export async function fetchApplicationCredentialById({
  applicationId,
  credentialId,
}: {
  applicationId: string;
  credentialId: string;
}): Promise<ApplicationCredential | undefined> {
  const authHeaders = await getAuthHeaders();
  const response = await applicationsClient.getCredentials({
    params: { id: applicationId },
    query: { decrypt: true, includeDisabled: false },
    overrideClientOptions: {
      baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
    },
    extraHeaders: authHeaders,
  });

  if (response.status !== 200) {
    console.warn(
      '[fetchApplicationCredentialById] Failed to fetch credentials',
      {
        applicationId,
        credentialId,
        status: response.status,
      },
    );

    return undefined;
  }

  const credentialsArray = response.body.credentials;

  if (!Array.isArray(credentialsArray)) {
    console.warn(
      '[fetchApplicationCredentialById] Credentials response was invalid',
      {
        applicationId,
        credentialId,
      },
    );

    return undefined;
  }

  const credential = credentialsArray.find(
    (entry) => entry.id === credentialId,
  );

  if (!credential) {
    console.warn('[fetchApplicationCredentialById] Credential not found', {
      applicationId,
      credentialId,
    });

    return undefined;
  }

  if (credential.isDisabled) {
    console.warn('[fetchApplicationCredentialById] Credential is disabled', {
      applicationId,
      credentialId,
    });

    return undefined;
  }

  return {
    id: credential.id,
    name: credential.name ?? null,
    role: credential.role,
    username: credential.username,
    loginUrl: credential.loginUrl ?? undefined,
    password: credential.password ?? undefined,
    basicAuthUsername: credential.basicAuthUsername ?? undefined,
    basicAuthPassword: credential.basicAuthPassword ?? undefined,
    mtlsCert: credential.mtlsCert ?? undefined,
    mtlsKey: credential.mtlsKey ?? undefined,
    mtlsPfx: credential.mtlsPfx ?? undefined,
    mtlsOrigin: credential.mtlsOrigin ?? undefined,
    mtlsPassphrase: credential.mtlsPassphrase ?? undefined,
    headers: credential.headers,
    cookies: credential.cookies.map((cookie) => ({
      name: cookie.name,
      value: cookie.value,
    })),
    secrets: credential.secrets,
    mfaConfig: (credential.mfaConfig ?? []).flatMap((cfg) => {
      if (!cfg.providerType) {
        return [];
      }

      return [
        {
          id: cfg.id,
          providerType: cfg.providerType,
          isActive: cfg.isActive,
        },
      ];
    }),
    isDisabled: credential.isDisabled,
    verification: credential.verification ?? undefined,
  };
}
